import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { yen } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { FileText, Receipt, Wrench, FolderOpen, MessageSquare, Loader2, ArrowRight, AlertCircle, UserCog } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const CARD = "bg-[#161616] border border-white/5 rounded-lg p-5";

const REQ_INDIVIDUAL = [["name", "氏名"], ["name_kana", "フリガナ"], ["birth_date", "生年月日"], ["phone", "電話番号"], ["email", "メールアドレス"], ["postal_code", "郵便番号"], ["prefecture", "都道府県"], ["city", "市区町村"], ["address_line", "番地"]];
const REQ_CORPORATE = [["company_name", "法人名"], ["company_kana", "フリガナ"], ["corporate_kind", "法人種別"], ["registration_number", "法人番号"], ["establishment_date", "設立年月日"], ["postal_code", "郵便番号"], ["prefecture", "都道府県"], ["city", "市区町村"], ["address_line", "番地"], ["phone", "電話番号"], ["email", "メールアドレス"], ["representative_title", "代表者役職"], ["rep_last_name", "代表者姓"], ["rep_first_name", "代表者名"], ["rep_last_kana", "代表者姓カナ"], ["rep_first_kana", "代表者名カナ"], ["representative_birth_date", "代表者生年月日"], ["representative_phone", "代表者電話"], ["rep_email", "代表者メール"], ["business_content", "事業内容"], ["industry", "業種"]];

function missingLabels(prof) {
  if (!prof) return [];
  const isCorp = prof.account_type === "corporate";
  const req = isCorp ? REQ_CORPORATE : REQ_INDIVIDUAL;
  const missing = req.filter(([k]) => !prof[k]).map(([, label]) => label);
  if (!isCorp && !(Array.isArray(prof.roles) && prof.roles.length)) missing.push("役割");
  return missing;
}

function ProfileAlert({ prof }) {
  const missing = missingLabels(prof);
  if (!prof || missing.length === 0) return null;
  return (
    <div className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4" data-testid="tenant-profile-alert">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-amber-200">プロフィールの入力が完了していません</div>
          <div className="text-xs text-amber-100/80 mt-1">
            未入力の項目が {missing.length} 件あります：{missing.slice(0, 8).join("・")}{missing.length > 8 ? " ほか" : ""}
          </div>
          <Link to="/tenant/profile" className="inline-flex items-center gap-1.5 mt-3 bg-amber-500 hover:bg-amber-600 text-black text-sm font-medium rounded-md px-4 py-2 transition-colors" data-testid="tenant-profile-alert-cta">
            <UserCog className="w-4 h-4" />プロフィールを入力する
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function TenantDashboard() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [prof, setProf] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => { api.get("/tenant/summary").then(({ data }) => setS(data)).catch(() => setS(false)); }, []);
  useEffect(() => {
    api.get("/tenant/profile").then(({ data }) => {
      setProf(data);
      if (missingLabels(data).length > 0) setModalOpen(true);
    }).catch(() => setProf(false));
  }, []);
  if (s === null) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  const missing = missingLabels(prof);

  const items = [
    { to: "/tenant/invoices", icon: Receipt, label: "未払い残高", value: yen(s?.unpaid_amount || 0), tone: (s?.unpaid_amount || 0) > 0 ? "text-red-400" : "text-emerald-400" },
    { to: "/tenant/contracts", icon: FileText, label: "契約", value: `${s?.contracts || 0} 件` },
    { to: "/tenant/repairs", icon: Wrench, label: "対応中の修繕", value: `${s?.open_repairs || 0} 件` },
    { to: "/tenant/documents", icon: FolderOpen, label: "書類", value: `${s?.documents || 0} 件` },
  ];

  const links = [
    { to: "/tenant/contracts", icon: FileText, label: "契約内容・電子契約を確認", desc: "契約情報の確認と、電子契約の署名・提出" },
    { to: "/tenant/invoices", icon: Receipt, label: "家賃・お支払い状況", desc: "請求と入金の履歴を確認" },
    { to: "/tenant/repairs", icon: Wrench, label: "修繕・不具合を依頼", desc: "設備の不具合を写真付きで申請・進捗確認" },
    { to: "/tenant/documents", icon: FolderOpen, label: "書類をダウンロード", desc: "契約書などの書類を確認" },
    { to: "/tenant/contact", icon: MessageSquare, label: "管理会社へお問い合わせ", desc: s?.unread_messages ? `未読 ${s.unread_messages} 件` : "メッセージのやり取り" },
  ];

  return (
    <div className="animate-fade-in" data-testid="tenant-dashboard">
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="tenant-profile-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertCircle className="w-5 h-5 text-amber-500" />{t("プロフィール未入力のお知らせ")}</DialogTitle>
            <DialogDescription>
              お客様のプロフィールに未入力の項目が {missing.length} 件あります。<br />
              スムーズなお手続きのため、ご登録をお願いします。
            </DialogDescription>
          </DialogHeader>
          {missing.length > 0 && (
            <div className="text-sm text-muted-foreground bg-amber-500/10 border border-amber-500/20 rounded-md p-3">
              {missing.slice(0, 10).join("・")}{missing.length > 10 ? " ほか" : ""}
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded-md border border-input hover:bg-accent" data-testid="tenant-profile-modal-later">{t("後で")}</button>
            <button onClick={() => { setModalOpen(false); navigate("/tenant/profile"); }} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-brand hover:bg-brand-hover text-white" data-testid="tenant-profile-modal-cta">
              <UserCog className="w-4 h-4" />{t("入力する")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <h1 className="font-display text-2xl font-light mb-1">{t("マイポータル")}</h1>
      <p className="text-sm text-gray-400 mb-6">{t("契約・家賃・修繕・書類をオンラインで管理できます。")}</p>

      <ProfileAlert prof={prof} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {items.map((it) => (
          <Link key={it.to} to={it.to} className={`${CARD} hover:border-brand/40 transition-colors`} data-testid={`tenant-stat-${it.to}`}>
            <div className="flex items-center justify-between mb-3"><it.icon className="w-5 h-5 text-brand" /></div>
            <div className={`font-display text-2xl font-light ${it.tone || ""}`}>{it.value}</div>
            <div className="text-xs text-gray-500 mt-1">{it.label}</div>
          </Link>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {links.map((l) => (
          <Link key={l.to} to={l.to} className={`${CARD} flex items-center gap-4 hover:border-brand/40 transition-colors group`} data-testid={`tenant-link-${l.to}`}>
            <span className="w-11 h-11 rounded-md bg-brand/15 text-brand flex items-center justify-center shrink-0"><l.icon className="w-5 h-5" /></span>
            <div className="flex-1">
              <div className="text-sm font-medium">{l.label}</div>
              <div className="text-xs text-gray-500">{l.desc}</div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-brand transition-colors" />
          </Link>
        ))}
      </div>
    </div>
  );
}
